import { useState, useRef, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useGetProfilePicture from "../../hooks/useGetProfilePicture";
import useGetAllUsers from "../../hooks/useGetAllUsers";
import useGetAllHashTag from "../../hooks/useGetAllHashTagList";
import useGetUserPoint from "../../hooks/useGetUserPoint";
import useCanGiveCoins from "../../hooks/useCanGiveCoins";
import {
  CommentPostFooterProps,
  CommentPostProps,
  CreateUserProps,
  HashTagListProps,
  TokenPayload,
} from "../../types/ComponentProps";
import Spinner from "../spinner/Spinner";
import RecipientAvatar from "../recipientButton/RecipientAvatar";
import addComment from "../../api/addComment";
import CommentsModalStyles from "./CommentsModalStyles.module.css";
import styles from "./CommentMentionInput.module.css";

type DropdownMode = "mention" | "hashtag" | "points" | "coins" | null;

const PRESET_AMOUNTS = [5, 10, 15, 20, 25];
const PRESET_COINS = [5, 10, 25, 50, 100];

const CommentPost = ({ post }: CommentPostFooterProps) => {
  const userDetails = jwtDecode<TokenPayload>(
    JSON.stringify(localStorage.getItem("accessTokenGC"))
  );

  const { getProfileImage } = useGetProfilePicture(Number(userDetails?.id));
  const { getAllUsersData } = useGetAllUsers();
  const { getAllHashTagListData } = useGetAllHashTag();
  const { data: userPointData } = useGetUserPoint();
  const canGiveCoins = useCanGiveCoins();

  const queryClient = useQueryClient();

  const [postLoading, setPostLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  // Editor state
  const editorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);
  const selectedUsersRef = useRef<CreateUserProps[]>([]);

  const [suggestions, setSuggestions] = useState<CreateUserProps[]>([]);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<HashTagListProps[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownMode, setDropdownMode] = useState<DropdownMode>(null);
  const [dropdownPos, setDropdownPos] = useState<{ left: number; top: number }>(
    { left: 0, top: 0 }
  );
  const [customAmount, setCustomAmount] = useState<string>("");
  const [customCoins, setCustomCoins] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // Scroll active dropdown item into view
  useEffect(() => {
    if (activeIndex >= 0 && dropdownRef.current) {
      const prefix =
        dropdownMode === "mention"
          ? "comment-mention-option"
          : dropdownMode === "hashtag"
            ? "comment-hashtag-option"
            : dropdownMode === "coins"
              ? "comment-coins-option"
              : "comment-points-option";
      const activeElement = dropdownRef.current.querySelector(
        `#${prefix}-${activeIndex}`
      );
      if (activeElement) {
        activeElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, dropdownMode]);

  const users: CreateUserProps[] = getAllUsersData?.users || [];
  const filteredUsersList = users.filter(
    (user) => user.id !== userDetails?.id
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        editorRef.current &&
        !editorRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setDropdownMode(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPlainText = (): string => {
    if (!editorRef.current) return "";
    let text = "";
    editorRef.current.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || "";
      } else if (node instanceof HTMLElement && node.dataset.mention) {
        text += `@${node.dataset.nickname}`;
      } else if (node instanceof HTMLElement && node.dataset.hashtag) {
        text += `#${node.getAttribute("data-hashtag-name") || ""}`;
      } else if (node instanceof HTMLElement && node.dataset.points) {
        text += `+${node.dataset.amount || "0"}`;
      } else if (node instanceof HTMLElement && node.dataset.coins) {
        text += `$${node.dataset.coinAmount || "0"}`;
      } else if (node instanceof HTMLElement) {
        text += node.textContent || "";
      }
    });
    return text;
  };

  const getSelectedUsers = (): CreateUserProps[] => {
    if (!editorRef.current) return [];
    const chips = editorRef.current.querySelectorAll("[data-mention]");
    const userIds = Array.from(chips).map((chip) =>
      Number(chip.getAttribute("data-user-id"))
    );
    return selectedUsersRef.current.filter((u) =>
      userIds.includes(Number(u.id))
    );
  };

  const getPointsGiven = (): number => {
    if (!editorRef.current) return 0;
    const chip = editorRef.current.querySelector("[data-points]");
    if (chip) {
      return Number(chip.getAttribute("data-amount")) || 0;
    }
    return 0;
  };

  const getCoinsGiven = (): number => {
    if (!editorRef.current) return 0;
    const chip = editorRef.current.querySelector("[data-coins]");
    if (chip) {
      return Number(chip.getAttribute("data-coin-amount")) || 0;
    }
    return 0;
  };

  const getSelectedHashTags = (): { hashTagId?: number; hashTagName?: string }[] => {
    if (!editorRef.current) return [];
    const chips = editorRef.current.querySelectorAll("[data-hashtag]");
    return Array.from(chips).map((chip) => ({
      hashTagId: Number(chip.getAttribute("data-hashtag-id")),
      hashTagName: chip.getAttribute("data-hashtag-name") || "",
    }));
  };

  const getCaretPosition = (): { left: number; top: number } => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editorRef.current)
      return { left: 0, top: 0 };

    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    const rect = range.getClientRects()[0];
    if (!rect) return { left: 0, top: 0 };

    const editorRect = editorRef.current.getBoundingClientRect();
    return {
      left: rect.left - editorRect.left,
      top: rect.bottom - editorRect.top + 4,
    };
  };

  const getCurrentTrigger = (): {
    trigger: DropdownMode;
    query: string;
  } | null => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;

    if (node.nodeType !== Node.TEXT_NODE) return null;

    const text = node.textContent || "";
    const cursorPos = range.startOffset;
    const textBeforeCursor = text.slice(0, cursorPos);

    // Check for @ mention
    const atMatch = textBeforeCursor.match(/@(\S*)$/);
    if (atMatch) return { trigger: "mention", query: atMatch[1] };

    // Check for # hashtag
    const hashMatch = textBeforeCursor.match(/#(\S*)$/);
    if (hashMatch) return { trigger: "hashtag", query: hashMatch[1] };

    // Check for + points
    const plusMatch = textBeforeCursor.match(/\+(\S*)$/);
    if (plusMatch) return { trigger: "points", query: plusMatch[1] };

    // Check for $ coins - only if user is authorized to give coins
    const dollarMatch = textBeforeCursor.match(/\$(\S*)$/);
    if (dollarMatch && canGiveCoins) return { trigger: "coins", query: dollarMatch[1] };

    return null;
  };

  const handleInput = () => {
    if (hasError) setHasError(false);

    // Save current selection
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }

    const triggerInfo = getCurrentTrigger();

    if (triggerInfo) {
      const { trigger, query } = triggerInfo;
      setDropdownMode(trigger);
      setDropdownPos(getCaretPosition());
      setShowDropdown(true);
      setActiveIndex(-1);

      if (trigger === "mention") {
        const alreadySelectedIds = selectedUsersRef.current.map((u) => u.id);
        const lowerQuery = query.toLowerCase();
        const filtered = filteredUsersList.filter((user) => {
          const isAlreadySelected = alreadySelectedIds.includes(user.id);
          const matchesQuery =
            user.preferredName.toLowerCase().startsWith(lowerQuery) ||
            user.firstName.toLowerCase().startsWith(lowerQuery) ||
            user.lastName.toLowerCase().startsWith(lowerQuery);
          return matchesQuery && !isAlreadySelected;
        });
        setSuggestions(filtered);
      } else if (trigger === "hashtag") {
        const lowerQuery = query.toLowerCase();
        const existingChipIds = new Set(
          Array.from(
            editorRef.current?.querySelectorAll("[data-hashtag]") || []
          ).map((el) => Number(el.getAttribute("data-hashtag-id")))
        );
        const allHashtags = getAllHashTagListData?.hashTagList || [];
        const filtered = allHashtags.filter(
          (tag: HashTagListProps) =>
            !existingChipIds.has(Number(tag.hashTagId)) &&
            (tag.hashTagName || "").toLowerCase().startsWith(lowerQuery)
        );
        setHashtagSuggestions(filtered);
      } else if (trigger === "points") {
        if (editorRef.current?.querySelector("[data-points]")) {
          setShowDropdown(false);
          setDropdownMode(null);
        } else {
          setCustomAmount(query);
        }
      } else if (trigger === "coins") {
        // Double-check authorization for coins
        if (!canGiveCoins) {
          setShowDropdown(false);
          setDropdownMode(null);
        } else if (editorRef.current?.querySelector("[data-coins]")) {
          setShowDropdown(false);
          setDropdownMode(null);
        } else {
          setCustomCoins(query);
        }
      }
    } else {
      setShowDropdown(false);
      setDropdownMode(null);
      setActiveIndex(-1);
    }
  };

  const replaceTriggerText = (chipElement: HTMLElement) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;

    if (node.nodeType !== Node.TEXT_NODE) return;

    const text = node.textContent || "";
    const cursorPos = range.startOffset;
    const textBeforeCursor = text.slice(0, cursorPos);
    const textAfterCursor = text.slice(cursorPos);

    // Find the trigger position (last @, #, +, or $)
    let triggerIndex = -1;
    const triggers = ["@", "#", "+", "$"];
    for (const t of triggers) {
      const idx = textBeforeCursor.lastIndexOf(t);
      if (idx > triggerIndex) triggerIndex = idx;
    }
    if (triggerIndex === -1) return;

    const beforeTrigger = textBeforeCursor.slice(0, triggerIndex);
    const parent = node.parentNode;
    if (!parent) return;

    const beforeNode = document.createTextNode(beforeTrigger);
    const afterNode = document.createTextNode(` ${textAfterCursor}`);

    parent.replaceChild(afterNode, node);
    parent.insertBefore(chipElement, afterNode);
    parent.insertBefore(beforeNode, chipElement);

    // Move cursor after the space
    const newRange = document.createRange();
    newRange.setStart(afterNode, 1);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
  };

  const insertMention = (user: CreateUserProps) => {
    const nickname = user.preferredName.replace(/\s+/g, "");
    const chip = document.createElement("span");
    chip.className = styles.mentionChip;
    chip.contentEditable = "false";
    chip.dataset.mention = "true";
    chip.dataset.userId = String(user.id);
    chip.dataset.nickname = nickname;
    chip.textContent = `@${user.preferredName}`;
    replaceTriggerText(chip);
    if (!selectedUsersRef.current.some((u) => u.id === user.id)) {
      selectedUsersRef.current = [...selectedUsersRef.current, user];
    }
    setShowDropdown(false);
    setDropdownMode(null);
  };

  const insertHashtag = (hashtag: HashTagListProps) => {
    const chip = document.createElement("span");
    chip.className = styles.hashtagChip;
    chip.contentEditable = "false";
    chip.dataset.hashtag = "true";
    chip.dataset.hashtagId = String(hashtag.hashTagId);
    chip.dataset.hashtagName = hashtag.hashTagName || "";
    chip.textContent = `#${hashtag.hashTagName}`;
    replaceTriggerText(chip);
    setShowDropdown(false);
    setDropdownMode(null);
  };

  const insertPoints = (amount: number) => {
    if (editorRef.current) {
      const existingChip = editorRef.current.querySelector("[data-points]");
      if (existingChip) existingChip.remove();
    }
    if (savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      }
    }
    const chip = document.createElement("span");
    chip.className = styles.pointsChip;
    chip.contentEditable = "false";
    chip.dataset.points = "true";
    chip.dataset.amount = String(amount);
    chip.textContent = `+${amount}`;
    replaceTriggerText(chip);
    setShowDropdown(false);
    setDropdownMode(null);
    setCustomAmount("");
  };

  const insertCoins = (amount: number) => {
    if (editorRef.current) {
      const existingChip = editorRef.current.querySelector("[data-coins]");
      if (existingChip) existingChip.remove();
    }
    if (savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      }
    }
    const chip = document.createElement("span");
    chip.className = styles.coinsChip;
    chip.contentEditable = "false";
    chip.dataset.coins = "true";
    chip.dataset.coinAmount = String(amount);
    chip.textContent = `$${amount}`;
    replaceTriggerText(chip);
    setShowDropdown(false);
    setDropdownMode(null);
    setCustomCoins("");
  };

  const removeChip = (chip: HTMLElement) => {
    chip.remove();
  };

  const isChipNode = (node: any): boolean => {
    return (
      node instanceof HTMLElement &&
      !!(node.dataset.mention ||
        node.dataset.hashtag ||
        node.dataset.points ||
        node.dataset.coins)
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showDropdown) {
      if (dropdownMode === "mention" && suggestions.length > 0) {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < suggestions.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : suggestions.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertMention(suggestions[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
      if (dropdownMode === "hashtag" && hashtagSuggestions.length > 0) {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < hashtagSuggestions.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : hashtagSuggestions.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertHashtag(hashtagSuggestions[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
      if (dropdownMode === "points") {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < PRESET_AMOUNTS.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : PRESET_AMOUNTS.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertPoints(PRESET_AMOUNTS[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
      if (dropdownMode === "coins") {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < PRESET_COINS.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : PRESET_COINS.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertCoins(PRESET_COINS[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
    }

    if (e.key === "Enter" && !showDropdown) { e.preventDefault(); }

    if (e.key === "Backspace") {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      if (!range.collapsed) return;
      const node = range.startContainer;
      const offset = range.startOffset;
      if (node.nodeType === Node.TEXT_NODE && offset === 0) {
        const prev = node.previousSibling;
        if (isChipNode(prev)) { e.preventDefault(); removeChip(prev as HTMLElement); return; }
      }
      if (node === editorRef.current && offset > 0) {
        const childBefore = node.childNodes[offset - 1];
        if (isChipNode(childBefore)) { e.preventDefault(); removeChip(childBefore as HTMLElement); return; }
      }
    }

    if (e.key === "Delete") {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      if (!range.collapsed) return;
      const node = range.startContainer;
      const offset = range.startOffset;
      if (node === editorRef.current) {
        const childAfter = node.childNodes[offset];
        if (isChipNode(childAfter)) { e.preventDefault(); removeChip(childAfter as HTMLElement); return; }
      }
      if (node.nodeType === Node.TEXT_NODE && offset === (node.textContent || "").length) {
        const next = node.nextSibling;
        if (isChipNode(next)) { e.preventDefault(); removeChip(next as HTMLElement); return; }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const addCommentMutation = useMutation({
    mutationFn: (commentData: CommentPostProps) =>
      addComment(commentData, post?.postId ?? 0),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getPostComments"] });
      await queryClient.invalidateQueries({ queryKey: ["getUserPoint"] });
      await queryClient.invalidateQueries({ queryKey: ["getLeaderBoardList"] });
      setPostLoading(false);
    },
    onError: (error: Error) => {
      console.error("Error creating comment:", error.message);
      setPostLoading(false);
    },
  });

  const onResetHandler = () => {
    if (editorRef.current) { editorRef.current.innerHTML = ""; }
    selectedUsersRef.current = [];
    setHasError(false);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const message = getPlainText().trim();
    const recipients = getSelectedUsers();
    const pointsGiven = getPointsGiven();
    const coinsGiven = getCoinsGiven();
    const hashTagIds = getSelectedHashTags();

    if (!message && recipients.length === 0 && !pointsGiven && !coinsGiven) {
      setHasError(true);
      return;
    }
    if (pointsGiven > 0 && recipients.length === 0) { setHasError(true); return; }
    if (coinsGiven > 0 && recipients.length === 0) { setHasError(true); return; }

    setPostLoading(true);
    try {
      await addCommentMutation.mutateAsync({
        message,
        pointsGiven: pointsGiven > 0 ? pointsGiven : undefined,
        coinsGiven: coinsGiven > 0 ? coinsGiven : undefined,
        recipients: recipients.length > 0 ? recipients : undefined,
        hashTagIds: hashTagIds.length > 0 ? hashTagIds : undefined,
      });
      if (editorRef.current) { editorRef.current.innerHTML = ""; }
      selectedUsersRef.current = [];
    } catch (error) {
      console.error("Error creating comment:", error);
    }
  };

  const renderMentionDropdown = () => (
    <>
      {suggestions.length === 0 ? (
        <div style={{ textAlign: "center", color: "#7d7d7d", fontSize: "12px", padding: "8px 0" }}>No users found</div>
      ) : (
        suggestions.map((user, index) => (
          <div key={user.id} id={`comment-mention-option-${index}`}
            className={`${styles.dropdownItem} ${index === activeIndex ? styles.dropdownItemActive : ""}`}
            onMouseDown={(e) => e.preventDefault()} onClick={() => insertMention(user)}
            onMouseEnter={() => setActiveIndex(index)} role="option" aria-selected={index === activeIndex}
            aria-label={`${user.firstName} ${user.lastName}, ${user.email}`}>
            <div className={styles.userListBlk}>
              <div><RecipientAvatar userId={Number(user.id)} /></div>
              <div><h3>{user.firstName} {user.lastName}</h3><span>{user.email}</span></div>
            </div>
          </div>
        ))
      )}
    </>
  );

  const renderHashtagDropdown = () => (
    <>
      {hashtagSuggestions.length === 0 ? (
        <div style={{ textAlign: "center", color: "#7d7d7d", fontSize: "12px", padding: "8px 0" }}>No hashtags found</div>
      ) : (
        hashtagSuggestions.map((tag, index) => (
          <div key={tag.hashTagId} id={`comment-hashtag-option-${index}`}
            className={`${styles.dropdownItem} ${index === activeIndex ? styles.dropdownItemActive : ""}`}
            onMouseDown={(e) => e.preventDefault()} onClick={() => insertHashtag(tag)}
            onMouseEnter={() => setActiveIndex(index)} role="option" aria-selected={index === activeIndex}
            aria-label={`Hashtag ${tag.hashTagName}`}>
            <span className={styles.hashtagSuggestionText}>#{tag.hashTagName}</span>
          </div>
        ))
      )}
    </>
  );

  const renderPointsDropdown = () => (
    <div className={styles.pointsDropdownContent} role="group" aria-label="Select points amount">
      <div className={styles.pointsPresets} role="listbox" aria-label="Preset amounts">
        {PRESET_AMOUNTS.map((amount, index) => (
          <button key={amount} id={`comment-points-option-${index}`}
            className={`${styles.pointsPresetBtn} ${index === activeIndex ? styles.pointsPresetBtnActive : ""}`}
            onMouseDown={(e) => e.preventDefault()} onClick={() => insertPoints(amount)}
            onMouseEnter={() => setActiveIndex(index)} type="button" role="option"
            aria-selected={index === activeIndex} aria-label={`Give ${amount} points`}>+{amount}</button>
        ))}
      </div>
      <div className={styles.pointsCustomRow}>
        <input type="number" placeholder="Custom amount" className={styles.pointsCustomInput}
          value={customAmount} onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => { const sel = window.getSelection(); if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) { savedSelectionRef.current = sel.getRangeAt(0).cloneRange(); } }}
          onChange={(e) => setCustomAmount(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && customAmount) { e.preventDefault(); const p = parseInt(customAmount, 10); if (!isNaN(p) && p > 0) insertPoints(p); } if (["e", "E", "-"].includes(e.key)) e.preventDefault(); }} />
        <button className={styles.pointsOkBtn} onMouseDown={(e) => e.preventDefault()}
          onClick={() => { const p = parseInt(customAmount, 10); if (!isNaN(p) && p > 0) insertPoints(p); }} type="button">OK</button>
      </div>
      <p className={styles.pointsAvailable}><span>{userPointData?.points?.giveablePoints || 0}</span> Points available to give</p>
    </div>
  );

  const renderCoinsDropdown = () => (
    <div className={styles.pointsDropdownContent} role="group" aria-label="Select coins amount">
      <div className={styles.pointsPresets} role="listbox" aria-label="Preset coin amounts">
        {PRESET_COINS.map((amount, index) => (
          <button key={amount} id={`comment-coins-option-${index}`}
            className={`${styles.coinsPresetBtn} ${index === activeIndex ? styles.pointsPresetBtnActive : ""}`}
            onMouseDown={(e) => e.preventDefault()} onClick={() => insertCoins(amount)}
            onMouseEnter={() => setActiveIndex(index)} type="button" role="option"
            aria-selected={index === activeIndex} aria-label={`Give ${amount} coins`}>${amount}</button>
        ))}
      </div>
      <div className={styles.pointsCustomRow}>
        <input type="number" placeholder="Custom coins" className={styles.pointsCustomInput}
          value={customCoins} onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => { const sel = window.getSelection(); if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) { savedSelectionRef.current = sel.getRangeAt(0).cloneRange(); } }}
          onChange={(e) => setCustomCoins(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && customCoins) { e.preventDefault(); const p = parseInt(customCoins, 10); if (!isNaN(p) && p > 0) insertCoins(p); } if (["e", "E", "-"].includes(e.key)) e.preventDefault(); }} />
        <button className={styles.pointsOkBtn} onMouseDown={(e) => e.preventDefault()}
          onClick={() => { const p = parseInt(customCoins, 10); if (!isNaN(p) && p > 0) insertCoins(p); }} type="button">OK</button>
      </div>
      <p className={styles.pointsAvailable}><span>{userPointData?.points?.coins || 0}</span> Coins available</p>
    </div>
  );

  return (
    <div className={CommentsModalStyles.postCommentBlkSection}>
      <div className={CommentsModalStyles.avatarCommentSection}>
        <img alt="profile" src={getProfileImage?.profileImage} />
      </div>
      <div className={`${styles.commentEditorWrapper} ${hasError ? styles.emptyOnSubmit : ""}`}>
        <form onSubmit={onSubmit}>
          <div ref={editorRef} className={styles.commentEditor} contentEditable
            onInput={handleInput} onKeyDown={handleKeyDown} onPaste={handlePaste}
            onBlur={() => { const sel = window.getSelection(); if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) { savedSelectionRef.current = sel.getRangeAt(0).cloneRange(); } }}
            data-placeholder="Write a comment.. (@ mention, # hashtag, + points, $ coins)"
            suppressContentEditableWarning role="combobox" aria-label="Write a comment"
            aria-multiline="true" aria-expanded={showDropdown} aria-haspopup="listbox"
            aria-controls={showDropdown ? "comment-suggestions-listbox" : undefined} />
          {showDropdown && (
            <div ref={dropdownRef} id="comment-suggestions-listbox" className={styles.dropdown}
              style={{ left: dropdownPos.left, top: dropdownPos.top }} role="listbox"
              aria-label={dropdownMode === "mention" ? "User suggestions" : dropdownMode === "hashtag" ? "Hashtag suggestions" : dropdownMode === "coins" ? "Coins options" : "Points options"}>
              {dropdownMode === "mention" && renderMentionDropdown()}
              {dropdownMode === "hashtag" && renderHashtagDropdown()}
              {dropdownMode === "points" && renderPointsDropdown()}
              {dropdownMode === "coins" && renderCoinsDropdown()}
            </div>
          )}
          <div className={styles.commentFooter}>
            <button className={styles.cancelButton} type="reset" onClick={onResetHandler}>Cancel</button>
            <button type="submit" className={styles.submitButton}>
              {postLoading ? <Spinner variant="small-spinner" alignSpin="center-spinner" /> : <span className="fa-solid fa-arrow-right"></span>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommentPost;
